import { Routes, Route, Navigate } from 'react-router-dom';
import { Frame } from '@shopify/polaris';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Site } from './pages/Site';
import { Setup } from './pages/Setup';
import { SetupScript } from './pages/SetupScript';
import { StandardReports } from './pages/StandardReports';
import { ReportsSiteSelection } from './pages/ReportsSiteSelection';
import { Help } from './pages/Help';
import { Pricing } from './pages/Pricing';
import { Account } from './pages/Account';
import { Onboarding } from './pages/Onboarding';
import { ProvisionCallback } from './pages/ProvisionCallback';
import { NotFound } from './pages/NotFound';

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public route wrapper (redirect to dashboard if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        Loading...
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Frame>
      <Routes>
        {/* Auth routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/login_email"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/site/:siteId"
          element={
            <ProtectedRoute>
              <Site />
            </ProtectedRoute>
          }
        />
        <Route
          path="/site/:siteId/setup"
          element={
            <ProtectedRoute>
              <Setup />
            </ProtectedRoute>
          }
        />
        <Route
          path="/site/:siteId/setup-script"
          element={
            <ProtectedRoute>
              <SetupScript />
            </ProtectedRoute>
          }
        />
        <Route
          path="/site/:siteId/standard-reports"
          element={
            <ProtectedRoute>
              <StandardReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <Help />
            </ProtectedRoute>
          }
        />
        <Route
          path="/standard-reports"
          element={
            <ProtectedRoute>
              <ReportsSiteSelection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pricing"
          element={
            <ProtectedRoute>
              <Pricing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          }
        />

        {/* GA4 account provisioning callback (Google redirects here after ToS) */}
        <Route
          path="/provision-callback"
          element={
            <ProtectedRoute>
              <ProvisionCallback />
            </ProtectedRoute>
          }
        />

        {/* Redirects */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Frame>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
